import React, { useState, useEffect } from 'react';
import { View, Text, StyleSheet, SafeAreaView, TextInput, TouchableOpacity, ScrollView, Alert, Image, ActivityIndicator } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Colors } from '../constants/theme';
import { useRouter } from 'expo-router';
import { useAppStore } from '../store/useAppStore';
import { firestoreService } from '../services/firestoreService';
import { volver } from '../utils/volver';
import { validarTelefono } from '../utils/telefono';
import { preguntarSiNo, avisar } from '../utils/preguntar';

export default function EditProfileScreen() {
  const router = useRouter();
  const storeName = useAppStore((state) => state.userName);
  const storePhone = useAppStore((state) => state.userPhone);
  const storeEmail = useAppStore((state) => state.userEmail);
  const userPhoto = useAppStore((state) => state.userPhoto);
  const setProfile = useAppStore((state) => state.setProfile);

  const isVerified = useAppStore((state) => state.isVerified);

  /**
   * ¿LA CEDULA YA CERRO EL NOMBRE?
   *
   * `nombreVerificado` lo estampa el SERVIDOR y solo cuando el admin tecleo el
   * nombre del documento al aprobar (functions/src/kyc.ts:624). Es distinto de
   * `isVerified`, que solo dice «identidad aprobada»: de las cuentas aprobadas
   * antes del 30-sep-2026, 19 de 24 tienen `isVerified` con el nombre que la
   * propia persona se puso. Por eso la pastilla tiene que mirar ESTE campo y
   * no aquel.
   *
   * No esta en el store a proposito: el store solo se refresca en el arranque
   * en frio, y una bandera rancia aqui es justo lo peligroso — si el admin
   * aprueba con la app abierta, el servidor ya piso el nombre y un cliente
   * viejo lo volveria a pisar al guardar. Una lectura al entrar, que es el
   * mismo patron que ya usa (tabs)/profile.tsx.
   */
  const [nombreCerrado, setNombreCerrado] = useState(false);
  useEffect(() => {
    let vivo = true;
    const uid = useAppStore.getState().userFirebaseUid;
    if (!uid) return;
    firestoreService
      .getUserProfile(uid)
      .then((perfil: any) => {
        if (vivo) setNombreCerrado(perfil?.nombreVerificado === true);
      })
      .catch((e: any) => {
        // Si no se pudo leer NO se cierra el campo: dejar a alguien sin poder
        // escribir su nombre por un bache de red es peor que dejarselo abierto,
        // porque el candado de verdad esta en las reglas y ahi no hay baches.
        console.warn('[edit-profile] no se pudo leer nombreVerificado:', e?.message);
      });
    return () => { vivo = false; };
  }, []);

  const [name, setName] = useState(storeName);
  const [phone, setPhone] = useState(storePhone);
  const [email, setEmail] = useState(storeEmail);
  const [saving, setSaving] = useState(false);

  // FIX SYNC — Antes el form capturaba storeName/storePhone/storeEmail al
  // MONTAR el componente, en useState(...). Si el KYC OCR escribía el
  // nombre real de la cédula al store DESPUÉS del montaje (caso real:
  // user abrió Edit Profile, completó KYC en otro tab, volvió), el form
  // seguía mostrando los valores viejos. Con useEffect sincronizamos
  // cada vez que el store cambia, pero solo si el user no está editando
  // activamente (heurística: aceptar el cambio del store solo si el field
  // local está vacío o coincide con el storeValue anterior).
  useEffect(() => {
    setName((prev) => (prev && prev !== storeName ? prev : storeName));
  }, [storeName]);
  useEffect(() => {
    setPhone((prev) => (prev && prev !== storePhone ? prev : storePhone));
  }, [storePhone]);
  useEffect(() => {
    setEmail((prev) => (prev && prev !== storeEmail ? prev : storeEmail));
  }, [storeEmail]);

  // QUE SE PUEDE ESCRIBIR AQUI, Y HASTA CUANDO.
  //
  // NOMBRE: se escribe libremente HASTA que la cedula lo cierre. Al aprobar el
  // KYC, el servidor pisa el nombre con el del documento y pone
  // `nombreVerificado: true` (functions/src/kyc.ts:624); a partir de ahi las
  // reglas deniegan cualquier cambio de `name`. Antes estaba bloqueado desde el
  // primer dia, y eso no protegia nada —el documento lo pisa igual— pero si
  // dejaba atrapado a quien entra con Google con el nombre de su cuenta de
  // Google, que puede ser una marca y no una persona.
  // TELEFONO: editable siempre; es un dato que puso la persona.
  // CORREO: fijo, es la identidad de la cuenta.
  // FOTO: sigue siendo la selfie del KYC — para cambiarla hay que re-verificar.
  const handleSave = async () => {
    // ══════════════════════════════════════════════════════════════════════
    // ESTA PANTALLA NO VALIDABA NADA. NI UNA COMPROBACIÓN.
    // ══════════════════════════════════════════════════════════════════════
    //
    // El teléfono se escribía tal cual se tecleara:
    // `updateUserProfile(uid, { phone })` con lo que hubiera, hasta
    // una cadena vacía. `add-phone.tsx` al menos contaba los dígitos; esta no.
    //
    // Y es la puerta ancha, porque es a la que se llega estando YA dentro:
    // «cuando uno ya está dentro de cualquier perfil iniciado con Google»
    // (fundador, 4-oct-2026). Quien entra con Google no trae teléfono, así
    // que este es el camino normal para ponerlo… y para borrarlo sin querer.
    //
    // Mismo validador que add-phone y que el registro de negocio: un solo
    // sitio decide qué es un teléfono. Ver la nota larga de add-phone.tsx
    // sobre lo que esto SÍ cierra (la forma) y lo que NO (que sea suyo, que
    // necesita un SMS y es una decisión de coste).
    // ── EL TELEFONO VACIO YA NO BLOQUEA EL NOMBRE ────────────────────────
    // Esto hacia `return` siempre que el telefono no fuera valido, y
    // `validarTelefono('')` devuelve ok:false. Quien entra con Google llega
    // SIN telefono, asi que no podia guardar absolutamente nada en esta
    // pantalla — ni su nombre. Ahora el telefono solo se valida si trae algo.
    const telEscrito = (phone ?? '').trim();
    let limpio: string | null = null;
    if (telEscrito !== '') {
      const tel = validarTelefono(telEscrito);
      if (!tel.ok) {
        await avisar('Revisa el número', tel.motivo);
        return;
      }
      // Se guardan los DIGITOS LIMPIOS, no el texto tal cual se tecleo. Si no,
      // el mismo numero escrito `809-555-1234` aqui y `8095551234` en otra
      // pantalla quedan como dos cosas distintas en la base de datos.
      limpio = tel.digitos;
    }

    // ── EL NOMBRE, SOLO SI EL CAMPO ESTA ABIERTO ─────────────────────────
    // El minimo de 3 letras es el mismo que exige el servidor al aprobar la
    // cedula (functions/src/kyc.ts). El tope de 120 lo exigen las reglas, y
    // sin comprobarlo aqui el guardado devolveria un permission-denied mudo.
    const nombreEscrito = (name ?? '').trim();
    if (!nombreCerrado) {
      if (nombreEscrito.length < 3) {
        await avisar('Falta tu nombre', 'Escribe tu nombre completo, como aparece en tu cédula.');
        return;
      }
      if (nombreEscrito.length > 120) {
        await avisar('Nombre muy largo', 'Tu nombre no puede pasar de 120 caracteres.');
        return;
      }
    }

    setSaving(true);
    setProfile(nombreCerrado ? name : nombreEscrito, limpio ?? phone, email);

    try {
      // MIGRADO DE RAILWAY → FIRESTORE DIRECTO.
      // Antes esto hacía POST a /api/users/profile-setup del backend de
      // Railway, que fue BORRADO (404 en todas sus rutas). El usuario
      // guardaba su teléfono y siempre leía "No se pudo guardar": el único
      // campo editable de esta pantalla no se guardaba nunca.
      //
      // Ni `phone` ni `name` están en la lista de campos de solo-servidor de
      // firestore.rules, así que el propio usuario puede escribirlos. El
      // nombre, eso sí, solo HASTA que la cédula lo cierre: en cuanto el
      // servidor pone `nombreVerificado`, la regla deniega el cambio. El
      // correo sigue siendo fijo: es la identidad de la cuenta.
      const uid = useAppStore.getState().userFirebaseUid;
      if (!uid) {
        await avisar('Sin sesión', 'Tu sesión expiró. Vuelve a iniciar sesión.');
        setSaving(false);
        return;
      }

      // Solo lo que de verdad cambio. Mandar `phone: null` borraria el que ya
      // tuviera, y mandar `name` con el campo cerrado seria un
      // permission-denied seguro.
      const cambios: Record<string, any> = {};
      if (limpio !== null) cambios.phone = limpio;
      if (!nombreCerrado && nombreEscrito !== storeName) cambios.name = nombreEscrito;
      if (Object.keys(cambios).length > 0) {
        await firestoreService.updateUserProfile(uid, cambios);
      }

      // La salida va FUERA del aviso: en react-native-web `Alert.alert` es una
      // función vacía, así que este `onPress` no se disparaba nunca y la
      // persona guardaba su teléfono y se quedaba mirando la misma pantalla,
      // sin saber si había funcionado. Es el mismo fallo que el registro de
      // negocio tenía en el aviso de éxito.
      await avisar('¡Listo!', 'Tu perfil se actualizó correctamente.');
      volver(router);
    } catch (e: any) {
      console.warn('[edit-profile] save falló:', e?.message);
      // Si el nombre ya quedo cerrado por la cedula mientras esta pantalla
      // estaba abierta, el servidor responde permission-denied. Decir
      // «verifica tu conexion» ahi manda a la persona a pelearse con su wifi
      // por algo que no es la red.
      const denegado = String(e?.code ?? '').includes('permission-denied');
      await avisar(
        'No se pudo guardar',
        denegado
          ? 'Tu nombre ya quedó fijado con tu cédula y no se puede cambiar desde aquí. Si hay un error en él, escríbenos y lo corregimos.'
          : 'Tus cambios quedaron en el teléfono, pero no llegaron al servidor. Revisa tu conexión y vuelve a intentar.'
      );
    } finally {
      setSaving(false);
    }
  };

  // Cambiar la foto → SIEMPRE pasa por re-verificación con la cédula. La
  // foto de perfil es la selfie biométrica comparada contra el documento;
  // no se puede reemplazar con una foto arbitraria de galería.
  const handleChangePhoto = () => {
    // ⚠️ Era `Alert.alert`: en react-native-web `Alert` es una función
    // vacía, así que en el navegador este botón no hacía nada. Ver utils/preguntar.ts.
    void preguntarSiNo(
      'Cambiar foto de perfil',
      isVerified
        ? 'Por seguridad, tu foto es la de tu verificación de identidad. Para cambiarla debes volver a verificar tu cédula con una nueva selfie en vivo. ¿Deseas continuar?'
        : 'Tu foto definitiva será la selfie de tu verificación de identidad. ¿Quieres verificarte ahora?',
      'Cancelar',
      'Verificar de nuevo'
    ).then((si: boolean) => { if (si) router.push('/kyc-documents'); });
  };

  return (
    <SafeAreaView style={styles.safeArea}>
      <View style={styles.header}>
        <TouchableOpacity onPress={() => router.canGoBack() ? router.back() : router.replace('/')} style={styles.backButton}>
          <Ionicons name="arrow-back" size={24} color={Colors.text} />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>Editar Perfil</Text>
        <View style={{ width: 40 }} />
      </View>

      <ScrollView contentContainerStyle={styles.container}>
        <View style={styles.avatarSection}>
          <View style={styles.avatarContainer}>
            {userPhoto ? (
              <Image source={{ uri: userPhoto }} style={styles.avatarImage} />
            ) : (
              <Ionicons name="person" size={60} color="#FFF" />
            )}
            {isVerified && (
              <View style={styles.verifiedBadge}>
                <Ionicons name="checkmark-circle" size={32} color="#4CAF50" />
              </View>
            )}
            <TouchableOpacity style={styles.editAvatarBtn} onPress={handleChangePhoto}>
              <Ionicons name="camera" size={20} color="#FFF" />
            </TouchableOpacity>
          </View>
          <Text style={styles.photoHint}>
            {isVerified ? (
              <>
                <Ionicons name="lock-closed" size={11} color={Colors.textSecondary} /> Foto verificada con tu cédula. Para cambiarla, verifica de nuevo.
              </>
            ) : (
              'Tu foto aún no está verificada. Cuando aprueben tu cédula se usará tu selfie.'
            )}
          </Text>
        </View>

        {/* NOMBRE — abierto hasta que la cédula lo cierre. Al aprobar el KYC
            el servidor lo pisa con el del documento y pone `nombreVerificado`;
            desde ahí las reglas deniegan cualquier cambio de `name`. */}
        <View style={styles.formGroup}>
          <View style={styles.lockedLabelRow}>
            <Text style={styles.label}>Nombre Completo</Text>
            {/* La pastilla mira `nombreVerificado`, NO `isVerified`: lo
                primero es «este nombre salio de un documento», lo segundo solo
                «la identidad esta aprobada». Antes se pintaba sin ninguna
                condicion y le decia «Verificado» a todo el mundo, incluido
                quien acababa de entrar con Google. */}
            {nombreCerrado ? (
              <View style={styles.lockedChip}>
                <Ionicons name="shield-checkmark" size={11} color="#00C853" />
                <Text style={styles.lockedChipText}>Verificado</Text>
              </View>
            ) : (
              /* Gris, NO verde: en esta pantalla el verde significa
                 «verificado» y esto es justo lo contrario. Con el mismo color
                 se leerían como lo mismo de un vistazo. */
              <View style={[styles.lockedChip, { backgroundColor: 'rgba(0,0,0,0.05)' }]}>
                <Ionicons name="create-outline" size={11} color={Colors.textSecondary} />
                <Text style={[styles.lockedChipText, { color: Colors.textSecondary }]}>Editable</Text>
              </View>
            )}
          </View>
          {nombreCerrado ? (
            <View style={[styles.input, styles.inputLocked]}>
              <Text style={{ color: Colors.text, fontSize: 16, flex: 1 }}>{name}</Text>
              <Ionicons name="lock-closed" size={16} color={Colors.textSecondary} />
            </View>
          ) : (
            <TextInput
              style={styles.input}
              value={name}
              onChangeText={setName}
              placeholder="Tu nombre completo"
              placeholderTextColor={Colors.textSecondary}
              autoCapitalize="words"
              maxLength={120}
            />
          )}
        </View>

        {/* TELÉFONO — editable (lo ingresó el usuario, no es del documento). */}
        <View style={styles.formGroup}>
          <Text style={styles.label}>Número de Teléfono</Text>
          <TextInput
            style={styles.input}
            value={phone}
            onChangeText={setPhone}
            keyboardType="phone-pad"
          />
        </View>

        {/* CORREO — BLOQUEADO. Es la identidad de la cuenta. */}
        <View style={styles.formGroup}>
          <View style={styles.lockedLabelRow}>
            <Text style={styles.label}>Correo Electrónico</Text>
            <View style={styles.lockedChip}>
              <Ionicons name="lock-closed" size={11} color={Colors.textSecondary} />
              <Text style={[styles.lockedChipText, { color: Colors.textSecondary }]}>Fijo</Text>
            </View>
          </View>
          <View style={[styles.input, styles.inputLocked]}>
            <Text style={{ color: Colors.textSecondary, fontSize: 16, flex: 1 }}>{email || '—'}</Text>
            <Ionicons name="lock-closed" size={16} color={Colors.textSecondary} />
          </View>
        </View>

        <TouchableOpacity style={[styles.saveBtn, saving && { opacity: 0.6 }]} onPress={handleSave} disabled={saving}>
          {saving ? (
            <ActivityIndicator color="#FFF" />
          ) : (
            <Text style={styles.saveBtnText}>Guardar cambios</Text>
          )}
        </TouchableOpacity>
      </ScrollView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: '#FFF',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 15,
    borderBottomWidth: 1,
    borderBottomColor: '#F0F0F0',
  },
  backButton: {
    padding: 8,
    marginLeft: -8,
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: Colors.text,
  },
  container: {
    padding: 20,
  },
  avatarSection: {
    alignItems: 'center',
    marginVertical: 30,
  },
  avatarContainer: {
    width: 120,
    height: 120,
    borderRadius: 60,
    backgroundColor: '#0A2540',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
  },
  editAvatarBtn: {
    position: 'absolute',
    bottom: 0,
    right: 0,
    backgroundColor: Colors.primary,
    width: 40,
    height: 40,
    borderRadius: 20,
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 3,
    borderColor: '#FFF',
  },
  verifiedBadge: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    backgroundColor: '#FFF',
    borderRadius: 16,
    padding: 2,
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
  },
  formGroup: {
    marginBottom: 20,
  },
  label: {
    fontSize: 14,
    color: Colors.textSecondary,
    marginBottom: 8,
    fontWeight: '600',
  },
  input: {
    backgroundColor: '#F5F7FA',
    borderRadius: 12,
    padding: 16,
    fontSize: 16,
    color: Colors.text,
    borderWidth: 1,
    borderColor: '#E1E8ED',
  },
  inputLocked: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#EEF1F5',
    borderColor: '#DCE1E8',
  },
  lockedLabelRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  lockedChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 3,
    backgroundColor: 'rgba(0,200,83,0.1)',
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 8,
  },
  lockedChipText: {
    fontSize: 11,
    fontWeight: '700',
    color: '#00A344',
    marginLeft: 2,
  },
  photoHint: {
    fontSize: 11.5,
    color: Colors.textSecondary,
    textAlign: 'center',
    marginTop: 12,
    paddingHorizontal: 20,
    lineHeight: 16,
  },
  saveBtn: {
    backgroundColor: Colors.primary,
    padding: 16,
    borderRadius: 12,
    alignItems: 'center',
    marginTop: 30,
  },
  saveBtnText: {
    color: '#FFF',
    fontSize: 16,
    fontWeight: 'bold',
  },
  avatarImage: {
    width: 120,
    height: 120,
    borderRadius: 60,
    resizeMode: 'cover',
  }
});

